import React, { useState, useEffect } from "react";
import Navbar from "./Navbar";
import { supabase } from "./supabase";

const fmt = (n) => "R" + Number(n || 0).toLocaleString("en-ZA");

const searchUsers = async (query) => {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(
    `${supabase.supabaseUrl}/functions/v1/search-users?q=${encodeURIComponent(query)}`,
    { headers: { Authorization: `Bearer ${session.access_token}` } }
  );
  const json = await res.json();
  if (json.error) throw new Error(json.error);
  return json.users;
};

const runVaultCheck = async (milestoneId) => {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(
    `${supabase.supabaseUrl}/functions/v1/check-milestone-rules`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ milestone_id: milestoneId }),
    }
  );
  const json = await res.json();
  if (json.error) throw new Error(json.error);
  return json;
};

export default function Admin() {
  const [activeTab, setActiveTab] = useState("overview");
  const [projects, setProjects] = useState([]);
  const [testimonies, setTestimonies] = useState([]);
  const [donations, setDonations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [evidenceByMilestone, setEvidenceByMilestone] = useState({});
  const [bankingDetails, setBankingDetails] = useState([]);
  const [expandedMilestone, setExpandedMilestone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [flagReasonFor, setFlagReasonFor] = useState(null);
  const [flagReasonText, setFlagReasonText] = useState("");

  const [leadPickerFor, setLeadPickerFor] = useState(null);
  const [leadSearch, setLeadSearch] = useState("");
  const [leadResults, setLeadResults] = useState([]);
  const [leadSearching, setLeadSearching] = useState(false);

  const [vaultResults, setVaultResults] = useState({});
  const [vaultChecking, setVaultChecking] = useState(null);

  useEffect(() => {
    checkAdmin();
  }, []);

  const checkAdmin = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { window.location.href = "/login"; return; }
    const { data } = await supabase.from("admins").select("*").eq("user_id", user.id);
    if (!data || data.length === 0) { window.location.href = "/"; return; }
    setIsAdmin(true);
    fetchAll();
  };

  const fetchAll = async () => {
    setErrorMsg("");
    try {
      const [p, t, d, s, m, b] = await Promise.all([
        supabase.from("projects").select("*").order("created_at", { ascending: false }),
        supabase.from("testimonies").select("*").order("created_at", { ascending: false }),
        supabase.from("donations").select("*, projects(title)").order("created_at", { ascending: false }),
        supabase.from("suppliers").select("*").order("created_at", { ascending: false }),
        supabase.from("milestones").select("*, projects(title), suppliers(business_name)").order("created_at", { ascending: false }),
        supabase.from("supplier_banking_details").select("*, suppliers(business_name)").order("created_at", { ascending: false }),
      ]);
      if (p.error || t.error || d.error || s.error || m.error || b.error) {
        throw new Error("Some data failed to load. Please refresh and try again.");
      }
      setProjects(p.data || []);
      setTestimonies(t.data || []);
      setDonations(d.data || []);
      setSuppliers(s.data || []);
      setMilestones(m.data || []);
      setBankingDetails(b.data || []);
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong loading the dashboard.");
    }
    setLoading(false);
  };

  const fetchEvidenceFor = async (milestoneId) => {
    try {
      const { data, error } = await supabase.from("evidence").select("*").eq("milestone_id", milestoneId).order("created_at", { ascending: false });
      if (error) throw error;
      setEvidenceByMilestone((prev) => ({ ...prev, [milestoneId]: data || [] }));
    } catch (err) {
      setErrorMsg("Could not load evidence for this milestone.");
    }
  };

  const toggleMilestone = (id) => {
    if (expandedMilestone === id) {
      setExpandedMilestone(null);
    } else {
      setExpandedMilestone(id);
      if (!evidenceByMilestone[id]) fetchEvidenceFor(id);
    }
  };

  const runAction = async (fn) => {
    setErrorMsg("");
    try {
      await fn();
      await fetchAll();
    } catch (err) {
      setErrorMsg(err.message || "Action failed. Nothing was changed.");
    }
  };

  const approveTestimony = (id) => runAction(async () => {
    const { error } = await supabase.from("testimonies").update({ approved: true }).eq("id", id);
    if (error) throw error;
  });

  const askDeleteTestimony = (id, name) => {
    setConfirmAction({
      label: `Delete the testimony from ${name}? This cannot be undone.`,
      onConfirm: () => runAction(async () => {
        const { error } = await supabase.from("testimonies").delete().eq("id", id);
        if (error) throw error;
      }),
    });
  };

  const approveProject = (id, title) => {
    setConfirmAction({
      label: `Approve "${title}"? It will become visible on the public Projects page immediately.`,
      onConfirm: () => runAction(async () => {
        const { error } = await supabase.from("projects").update({ status: "approved" }).eq("id", id);
        if (error) throw error;
      }),
    });
  };

  const askDeleteProject = (id, title) => {
    setConfirmAction({
      label: `Delete "${title}"? This removes the project permanently and cannot be undone.`,
      onConfirm: () => runAction(async () => {
        const { error } = await supabase.from("projects").delete().eq("id", id);
        if (error) throw error;
      }),
    });
  };

  const verifySupplier = (id) => runAction(async () => {
    const { error } = await supabase.from("suppliers").update({ verified: true }).eq("id", id);
    if (error) throw error;
  });

  const askDeleteSupplier = (id, name) => {
    setConfirmAction({
      label: `Delete "${name}" from the supplier directory? This cannot be undone.`,
      onConfirm: () => runAction(async () => {
        const { error } = await supabase.from("suppliers").delete().eq("id", id);
        if (error) throw error;
      }),
    });
  };

  const submitFlag = (evidenceId, milestoneId) => runAction(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    const { error: e1 } = await supabase.from("evidence").update({
      flagged_suspicious: true,
      flagged_by: user.id,
      flagged_reason: flagReasonText || "No reason provided",
    }).eq("id", evidenceId);
    if (e1) throw e1;

    const { error: e2 } = await supabase.from("milestones").update({ status: "flagged" }).eq("id", milestoneId);
    if (e2) throw e2;

    setFlagReasonFor(null);
    setFlagReasonText("");
  });

  const markBankingVerified = (id) => runAction(async () => {
    const { error } = await supabase.from("supplier_banking_details").update({
      avs_verified: true,
      avs_verified_at: new Date().toISOString(),
      avs_reference: "manual-verification",
    }).eq("id", id);
    if (error) throw error;
  });

  const handleLeadSearch = async (query) => {
    setLeadSearch(query);
    if (query.length < 2) { setLeadResults([]); return; }
    setLeadSearching(true);
    try {
      const results = await searchUsers(query);
      setLeadResults(results);
    } catch (err) {
      setErrorMsg("Could not search users: " + err.message);
    }
    setLeadSearching(false);
  };

  const assignLead = (projectId, userId) => runAction(async () => {
    const { error } = await supabase.from("projects").update({ created_by: userId }).eq("id", projectId);
    if (error) throw error;
    setLeadPickerFor(null);
    setLeadSearch("");
    setLeadResults([]);
  });

  const handleRunVaultCheck = async (milestoneId) => {
    setVaultChecking(milestoneId);
    setErrorMsg("");
    try {
      const result = await runVaultCheck(milestoneId);
      setVaultResults((prev) => ({ ...prev, [milestoneId]: result }));
      await fetchAll();
    } catch (err) {
      setErrorMsg("Vault check failed: " + err.message);
    }
    setVaultChecking(null);
  };

  const totalRaised = donations.reduce((sum, d) => sum + Number(d.amount || 0), 0);
  const pendingTestimonies = testimonies.filter((t) => !t.approved);
  const pendingSuppliers = suppliers.filter((s) => !s.verified);
  const pendingProjects = projects.filter((p) => p.status === "pending_review");
  const flaggedMilestones = milestones.filter((m) => m.status === "flagged");
  const pendingBanking = bankingDetails.filter((b) => !b.avs_verified);

  const statusColors = {
    approved: { bg: "rgba(46,125,50,0.08)", color: "#2E7D32" },
    pending_review: { bg: "rgba(201,168,76,0.12)", color: "#8a6a1a" },
    flagged: { bg: "rgba(192,57,43,0.1)", color: "#C0392B" },
  };

  const tabIcons = {
    overview:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/></svg>,
    projects:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6v6H9z"/></svg>,
    milestones:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>,
    testimonies: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
    donations:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M16 8h-6a2 2 0 000 4h4a2 2 0 010 4H8M12 6v2M12 16v2"/></svg>,
    suppliers:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l2-7h14l2 7"/><path d="M3 9v11a1 1 0 001 1h16a1 1 0 001-1V9"/><path d="M9 21V14h6v7"/></svg>,
    banking:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 019.9-1"/></svg>,
  };

  const statCards = [
    { label: "Total projects", value: projects.length },
    { label: "Pending projects", value: pendingProjects.length, alert: pendingProjects.length > 0 },
    { label: "Total raised", value: fmt(totalRaised) },
    { label: "Pending testimonies", value: pendingTestimonies.length },
    { label: "Pending suppliers", value: pendingSuppliers.length },
    { label: "Flagged milestones", value: flaggedMilestones.length, alert: flaggedMilestones.length > 0 },
    { label: "Banking unverified", value: pendingBanking.length },
  ];

  if (loading || !isAdmin) {
    return (
      <div style={{ background: "#F5F0E8", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 48, height: 48, background: "#1B3A6B", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2C8 6 6 10 8 14c-2-1-3-3-3-5C3 13 2 17 5 20c1.5 1.5 3.5 2 6 2 5 0 8-3 8-7 0-2-1-4-2.5-5.5C16 11 15 13 13 14c1-3 0-7-1-12z"/>
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "var(--font-body)", background: "#F5F0E8", minHeight: "100vh", color: "#1A1A2E" }}>

      <style>{`
        .admin-wrap { max-width: 1100px; margin: 0 auto; padding: 40px 32px 64px; }
        .admin-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 14px; margin-bottom: 28px; }
        .admin-tabs { display: flex; gap: 4px; margin-bottom: 24px; background: #fff; border: 1px solid #EDE8DF; border-radius: 99px; padding: 4px; overflow-x: auto; width: 100%; }
        .admin-tab-btn { display: flex; align-items: center; gap: 6px; background: transparent; border: none; border-radius: 99px; padding: 8px 16px; color: #888; font-weight: 600; font-size: 12px; cursor: pointer; text-transform: capitalize; font-family: var(--font-body); white-space: nowrap; flex-shrink: 0; }
        .admin-tab-btn.active { background: #1B3A6B; color: #fff; }
        .admin-overview { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; }
        .admin-card { background: #fff; border: 1px solid #EDE8DF; border-radius: 16px; padding: 24px; }
        .admin-btn-green { background: rgba(46,125,50,0.08); border: 1px solid rgba(46,125,50,0.2); border-radius: 6px; padding: 6px 12px; color: #2E7D32; font-size: 11px; font-weight: 700; cursor: pointer; font-family: var(--font-body); }
        .admin-btn-red { background: transparent; border: 1px solid rgba(192,57,43,0.3); border-radius: 6px; padding: 6px 12px; color: #C0392B; font-size: 11px; font-weight: 700; cursor: pointer; font-family: var(--font-body); }
        .admin-btn-gold { background: rgba(201,168,76,0.1); border: 1px solid rgba(201,168,76,0.3); border-radius: 6px; padding: 6px 12px; color: #8a6a1a; font-size: 11px; font-weight: 700; cursor: pointer; font-family: var(--font-body); }
        .admin-error-banner { background: rgba(192,57,43,0.08); border: 1px solid rgba(192,57,43,0.25); border-radius: 12px; padding: 14px 18px; color: #C0392B; font-size: 13px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center; gap: 12px; }
        .admin-modal-overlay { position: fixed; inset: 0; background: rgba(26,26,46,0.5); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 20px; }
        .admin-modal { background: #fff; border-radius: 16px; padding: 28px; max-width: 400px; width: 100%; }
        @media (max-width: 768px) {
          .admin-wrap { padding: 24px 16px 48px; }
          .admin-stats { grid-template-columns: 1fr 1fr; gap: 10px; }
          .admin-overview { grid-template-columns: 1fr; }
          .admin-card { padding: 18px 16px; }
        }
        @media (max-width: 480px) {
          .admin-stats { grid-template-columns: 1fr 1fr; }
        }
      `}</style>

      <Navbar />

      <div className="admin-wrap">

        {/* HEADER */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: "inline-block", background: "rgba(27,58,107,0.07)", border: "1px solid rgba(27,58,107,0.15)", borderRadius: 99, padding: "5px 16px", fontSize: 11, color: "#1B3A6B", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>
            Admin Panel
          </div>
          <h1 style={{ fontSize: "clamp(20px, 4vw, 26px)", fontWeight: 900, margin: 0, letterSpacing: "-0.02em" }}>
            Forge SA <span style={{ color: "#1B3A6B" }}>Control Centre</span>
          </h1>
        </div>

        {/* ERROR BANNER */}
        {errorMsg && (
          <div className="admin-error-banner">
            <span>{errorMsg}</span>
            <span style={{ cursor: "pointer", fontWeight: 700 }} onClick={() => setErrorMsg("")}>&times;</span>
          </div>
        )}

        {/* STATS */}
        <div className="admin-stats">
          {statCards.map((stat, i) => (
            <div key={i} style={{ background: "#fff", border: stat.alert ? "1px solid rgba(192,57,43,0.4)" : "1px solid #EDE8DF", borderRadius: 14, padding: 16 }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: stat.alert ? "#C0392B" : "#1B3A6B", marginBottom: 4 }}>{stat.value}</div>
              <div style={{ fontSize: 11, color: "#888" }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* TABS */}
        <div className="admin-tabs">
          {["overview", "projects", "milestones", "testimonies", "donations", "suppliers", "banking"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`admin-tab-btn${activeTab === tab ? " active" : ""}`}
            >
              {tabIcons[tab]}
              {tab}
            </button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === "overview" && (
          <div className="admin-overview">
            {pendingProjects.length > 0 && (
              <div className="admin-card" style={{ borderColor: "rgba(201,168,76,0.4)" }}>
                <h3 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 16px", color: "#8a6a1a" }}>Pending projects</h3>
                {pendingProjects.slice(0, 3).map((p, i) => (
                  <div key={i} style={{ padding: "12px 0", borderBottom: i < Math.min(2, pendingProjects.length - 1) ? "1px solid #EDE8DF" : "none" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1A1A2E", marginBottom: 4 }}>{p.title}</div>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>{p.category} &middot; {p.location}</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => approveProject(p.id, p.title)} className="admin-btn-green">Approve</button>
                      <button onClick={() => askDeleteProject(p.id, p.title)} className="admin-btn-red">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="admin-card">
              <h3 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 16px" }}>Recent donations</h3>
              {donations.slice(0, 5).map((d, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: i < Math.min(4, donations.length - 1) ? "1px solid #EDE8DF" : "none" }}>
                  <div style={{ fontSize: 13, color: "#888" }}>{d.projects?.title || "Project"}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1B3A6B" }}>{fmt(d.amount)}</div>
                </div>
              ))}
              {donations.length === 0 && <div style={{ fontSize: 13, color: "#888" }}>No donations yet.</div>}
            </div>

            {flaggedMilestones.length > 0 && (
              <div className="admin-card" style={{ borderColor: "rgba(192,57,43,0.3)" }}>
                <h3 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 16px", color: "#C0392B" }}>Flagged milestones, need review</h3>
                {flaggedMilestones.map((m, i) => (
                  <div key={i} style={{ padding: "10px 0", borderBottom: i < flaggedMilestones.length - 1 ? "1px solid #EDE8DF" : "none" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1A1A2E" }}>{m.title}</div>
                    <div style={{ fontSize: 12, color: "#888" }}>{m.projects?.title}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="admin-card">
              <h3 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 16px" }}>Pending testimonies</h3>
              {pendingTestimonies.length === 0 ? (
                <div style={{ fontSize: 13, color: "#888" }}>No pending testimonies.</div>
              ) : (
                pendingTestimonies.slice(0, 3).map((t, i) => (
                  <div key={i} style={{ padding: "12px 0", borderBottom: i < Math.min(2, pendingTestimonies.length - 1) ? "1px solid #EDE8DF" : "none" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1A1A2E", marginBottom: 4 }}>{t.name}, {t.province}</div>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 8, lineHeight: 1.5 }}>{t.story?.substring(0, 90)}...</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => approveTestimony(t.id)} className="admin-btn-green">Approve</button>
                      <button onClick={() => askDeleteTestimony(t.id, t.name)} className="admin-btn-red">Delete</button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="admin-card">
              <h3 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 16px" }}>Pending suppliers</h3>
              {pendingSuppliers.length === 0 ? (
                <div style={{ fontSize: 13, color: "#888" }}>No pending suppliers.</div>
              ) : (
                pendingSuppliers.slice(0, 3).map((s, i) => (
                  <div key={i} style={{ padding: "12px 0", borderBottom: i < Math.min(2, pendingSuppliers.length - 1) ? "1px solid #EDE8DF" : "none" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1A1A2E", marginBottom: 4 }}>{s.business_name}</div>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>{s.category} &middot; {s.province}</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => verifySupplier(s.id)} className="admin-btn-green">Verify</button>
                      <button onClick={() => askDeleteSupplier(s.id, s.business_name)} className="admin-btn-red">Delete</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* PROJECTS TAB */}
        {activeTab === "projects" && (
          <div className="admin-card">
            <h3 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 16px" }}>All projects</h3>
            {projects.map((p, i) => {
              const statusStyle = statusColors[p.status] || { bg: "rgba(136,136,136,0.1)", color: "#888" };
              return (
                <div key={i} style={{ padding: "16px 0", borderBottom: i < projects.length - 1 ? "1px solid #EDE8DF" : "none" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#1A1A2E", marginBottom: 2 }}>{p.title}</div>
                      <div style={{ fontSize: 12, color: "#888" }}>{p.location} &middot; {p.category}</div>
                      <div style={{ fontSize: 12, color: "#1B3A6B", fontWeight: 600, marginTop: 2 }}>{fmt(p.raised)} raised of {fmt(p.goal)}</div>
                      <div style={{ fontSize: 11, color: p.created_by ? "#2E7D32" : "#8a6a1a", marginTop: 4 }}>
                        {p.created_by ? "Lead assigned" : "No lead assigned yet"}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <div style={{ fontSize: 11, background: statusStyle.bg, color: statusStyle.color, borderRadius: 99, padding: "3px 12px", fontWeight: 700, textTransform: "capitalize" }}>
                        {p.status?.replace("_", " ")}
                      </div>
                      {p.status === "pending_review" && (
                        <button onClick={() => approveProject(p.id, p.title)} className="admin-btn-green">Approve</button>
                      )}
                      <button onClick={() => setLeadPickerFor(p.id)} className="admin-btn-gold">
                        {p.created_by ? "Change Lead" : "Assign Lead"}
                      </button>
                      <button onClick={() => askDeleteProject(p.id, p.title)} className="admin-btn-red">Delete</button>
                    </div>
                  </div>

                  {leadPickerFor === p.id && (
                    <div style={{ width: "100%", background: "#F5F0E8", borderRadius: 12, padding: 14, marginTop: 12 }}>
                      <input
                        value={leadSearch}
                        onChange={(e) => handleLeadSearch(e.target.value)}
                        placeholder="Search by name or email..."
                        style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #EDE8DF", fontSize: 13, fontFamily: "var(--font-body)", marginBottom: 10, boxSizing: "border-box" }}
                      />
                      {leadSearching && <div style={{ fontSize: 12, color: "#888" }}>Searching...</div>}
                      {leadResults.map((u) => (
                        <div
                          key={u.id}
                          onClick={() => assignLead(p.id, u.id)}
                          style={{ padding: "8px 10px", background: "#fff", borderRadius: 8, marginBottom: 6, cursor: "pointer", fontSize: 12 }}
                        >
                          <div style={{ fontWeight: 700 }}>{u.name}</div>
                          <div style={{ color: "#888" }}>{u.email}</div>
                        </div>
                      ))}
                      <button
                        onClick={() => { setLeadPickerFor(null); setLeadSearch(""); setLeadResults([]); }}
                        className="admin-btn-red"
                        style={{ marginTop: 4 }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            {projects.length === 0 && <div style={{ fontSize: 13, color: "#888" }}>No projects yet.</div>}
          </div>
        )}

        {/* MILESTONES TAB */}
        {activeTab === "milestones" && (
          <div className="admin-card">
            <h3 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 4px" }}>All milestones</h3>
            <p style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>
              Payments are never authorised by a person here. Forge Vault checks all 8 rules automatically, this only reviews evidence and flags concerns.
            </p>
            {milestones.length === 0 ? (
              <div style={{ fontSize: 13, color: "#888" }}>No milestones yet.</div>
            ) : (
              milestones.map((m) => (
                <div key={m.id} style={{ padding: "14px 0", borderBottom: "1px solid #EDE8DF" }}>
                  <div
                    onClick={() => toggleMilestone(m.id)}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", gap: 12, flexWrap: "wrap" }}
                  >
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#1A1A2E" }}>{m.title}</div>
                      <div style={{ fontSize: 12, color: "#888" }}>{m.projects?.title} &middot; Stage {m.stage_number} &middot; {m.suppliers?.business_name || "No supplier set"}</div>
                    </div>
                    <div style={{
                      fontSize: 11, fontWeight: 700, borderRadius: 99, padding: "3px 12px", textTransform: "capitalize",
                      background: m.status === "flagged" ? "rgba(192,57,43,0.1)" : "rgba(91,155,213,0.1)",
                      color: m.status === "flagged" ? "#C0392B" : "#185FA5",
                    }}>
                      {m.status?.replace("_", " ")}
                    </div>
                  </div>

                  {expandedMilestone === m.id && (
                    <div style={{ marginTop: 14, paddingLeft: 4 }}>
                      <div style={{ fontSize: 12, color: "#4A4A6A", marginBottom: 10 }}>
                        Budget: {fmt(m.approved_budget)} &middot; Invoice: {m.invoice_amount ? fmt(m.invoice_amount) : "Not submitted"}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#1A1A2E", marginBottom: 8 }}>Evidence</div>
                      {(evidenceByMilestone[m.id] || []).length === 0 ? (
                        <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>No evidence uploaded yet.</div>
                      ) : (
                        evidenceByMilestone[m.id].map((e) => (
                          <div key={e.id} style={{ background: "#F5F0E8", borderRadius: 10, padding: "10px 14px", marginBottom: 8 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: "#1A1A2E", textTransform: "capitalize" }}>{e.evidence_type}</div>
                                <a href={e.file_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "#1B3A6B" }}>View file</a>
                              </div>
                              {e.flagged_suspicious ? (
                                <span style={{ fontSize: 11, color: "#C0392B", fontWeight: 700 }}>Flagged</span>
                              ) : (
                                <button className="admin-btn-red" onClick={() => setFlagReasonFor(e.id)}>Flag as suspicious</button>
                              )}
                            </div>
                            {e.flagged_suspicious && e.flagged_reason && (
                              <div style={{ fontSize: 11, color: "#C0392B", marginTop: 6, fontStyle: "italic" }}>Reason: {e.flagged_reason}</div>
                            )}
                            {flagReasonFor === e.id && (
                              <div style={{ marginTop: 10 }}>
                                <textarea
                                  value={flagReasonText}
                                  onChange={(ev) => setFlagReasonText(ev.target.value)}
                                  placeholder="Why does this look suspicious? This pauses the whole milestone until resolved."
                                  style={{ width: "100%", fontSize: 12, padding: 8, borderRadius: 8, border: "1px solid #EDE8DF", fontFamily: "var(--font-body)", marginBottom: 8, resize: "vertical" }}
                                  rows={2}
                                />
                                <div style={{ display: "flex", gap: 8 }}>
                                  <button className="admin-btn-red" onClick={() => submitFlag(e.id, m.id)}>Confirm flag</button>
                                  <button className="admin-btn-gold" onClick={() => { setFlagReasonFor(null); setFlagReasonText(""); }}>Cancel</button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      )}

                      {/* FORGE VAULT CHECK */}
                      <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #EDE8DF" }}>
                        <button
                          onClick={() => handleRunVaultCheck(m.id)}
                          disabled={vaultChecking === m.id}
                          style={{ background: vaultChecking === m.id ? "#EDE8DF" : "#1B3A6B", border: "none", borderRadius: 99, padding: "10px 20px", color: vaultChecking === m.id ? "#888" : "#fff", fontWeight: 700, fontSize: 12, cursor: vaultChecking === m.id ? "not-allowed" : "pointer", fontFamily: "var(--font-body)" }}
                        >
                          {vaultChecking === m.id ? "Checking..." : "Run Vault Check"}
                        </button>

                        {vaultResults[m.id] && (
                          <div style={{ marginTop: 14 }}>
                            <div style={{
                              fontSize: 13, fontWeight: 800, marginBottom: 10,
                              color: vaultResults[m.id].allPassed ? "#2E7D32" : "#C0392B",
                            }}>
                              {vaultResults[m.id].allPassed ? "All 8 rules passed. Authorized for payment." : "Not all rules passed."}
                            </div>
                            {vaultResults[m.id].results.map((r) => (
                              <div key={r.rule_number} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "6px 0", fontSize: 12 }}>
                                <span style={{ color: r.passed ? "#2E7D32" : "#C0392B", fontWeight: 700, flexShrink: 0 }}>
                                  {r.passed ? "✓" : "✗"}
                                </span>
                                <div>
                                  <span style={{ fontWeight: 700, color: "#1A1A2E" }}>{r.rule_name}</span>
                                  <span style={{ color: "#888" }}> — {r.reason}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* TESTIMONIES TAB */}
        {activeTab === "testimonies" && (
          <div className="admin-card">
            <h3 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 16px" }}>All testimonies</h3>
            {testimonies.length === 0 ? (
              <div style={{ fontSize: 13, color: "#888" }}>No testimonies yet.</div>
            ) : (
              testimonies.map((t, i) => (
                <div key={i} style={{ padding: "18px 0", borderBottom: i < testimonies.length - 1 ? "1px solid #EDE8DF" : "none" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#1A1A2E", marginBottom: 2 }}>{t.name}, {t.province}</div>
                      <div style={{ fontSize: 12, color: "#1B3A6B", fontWeight: 600, marginBottom: 6 }}>{t.project}</div>
                      <div style={{ fontSize: 13, color: "#888", lineHeight: 1.65, wordBreak: "break-word" }}>{t.story}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
                      <div style={{ fontSize: 11, background: t.approved ? "rgba(46,125,50,0.08)" : "rgba(201,168,76,0.12)", color: t.approved ? "#2E7D32" : "#8a6a1a", borderRadius: 99, padding: "3px 10px", fontWeight: 700, textAlign: "center" }}>
                        {t.approved ? "Approved" : "Pending"}
                      </div>
                      {!t.approved && (
                        <button onClick={() => approveTestimony(t.id)} className="admin-btn-green">Approve</button>
                      )}
                      <button onClick={() => askDeleteTestimony(t.id, t.name)} className="admin-btn-red">Delete</button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* DONATIONS TAB */}
        {activeTab === "donations" && (
          <div className="admin-card">
            <h3 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 16px" }}>All donations</h3>
            <p style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>Donations are never deleted here, for audit reasons. That's intentional.</p>
            {donations.length === 0 ? (
              <div style={{ fontSize: 13, color: "#888" }}>No donations yet.</div>
            ) : (
              donations.map((d, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "14px 0", borderBottom: i < donations.length - 1 ? "1px solid #EDE8DF" : "none" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#1A1A2E", marginBottom: 2 }}>{d.projects?.title || "Project"}</div>
                    <div style={{ fontSize: 12, color: "#888" }}>{new Date(d.created_at).toLocaleDateString("en-ZA")}</div>
                    {d.message && <div style={{ fontSize: 12, color: "#888", fontStyle: "italic", marginTop: 2 }}>"{d.message}"</div>}
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 900, color: "#1B3A6B", flexShrink: 0 }}>{fmt(d.amount)}</div>
                </div>
              ))
            )}
          </div>
        )}

        {/* SUPPLIERS TAB */}
        {activeTab === "suppliers" && (
          <div className="admin-card">
            <h3 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 16px" }}>All suppliers</h3>
            {suppliers.length === 0 ? (
              <div style={{ fontSize: 13, color: "#888" }}>No suppliers registered yet.</div>
            ) : (
              suppliers.map((s, i) => (
                <div key={i} style={{ padding: "16px 0", borderBottom: i < suppliers.length - 1 ? "1px solid #EDE8DF" : "none" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#1A1A2E", marginBottom: 2 }}>{s.business_name}</div>
                      <div style={{ fontSize: 12, color: "#1B3A6B", fontWeight: 600, marginBottom: 4 }}>{s.category} &middot; {s.province}</div>
                      <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>{s.contact_info}</div>
                      <div style={{ fontSize: 13, color: "#888", lineHeight: 1.6, wordBreak: "break-word" }}>{s.description}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
                      <div style={{ fontSize: 11, background: s.verified ? "rgba(46,125,50,0.08)" : "rgba(201,168,76,0.12)", color: s.verified ? "#2E7D32" : "#8a6a1a", borderRadius: 99, padding: "3px 10px", fontWeight: 700, textAlign: "center" }}>
                        {s.verified ? "Verified" : "Pending"}
                      </div>
                      {!s.verified && (
                        <button onClick={() => verifySupplier(s.id)} className="admin-btn-green">Verify</button>
                      )}
                      <button onClick={() => askDeleteSupplier(s.id, s.business_name)} className="admin-btn-red">Delete</button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* BANKING TAB */}
        {activeTab === "banking" && (
          <div className="admin-card">
            <h3 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 4px" }}>Supplier banking details</h3>
            <p style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>
              Verification here is manual for now, Nedbank's Account Verification API integration is still pending. Once live, this becomes automatic.
            </p>
            {bankingDetails.length === 0 ? (
              <div style={{ fontSize: 13, color: "#888" }}>No banking details submitted yet.</div>
            ) : (
              bankingDetails.map((b) => (
                <div key={b.id} style={{ padding: "14px 0", borderBottom: "1px solid #EDE8DF" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#1A1A2E" }}>{b.suppliers?.business_name}</div>
                      <div style={{ fontSize: 12, color: "#888" }}>{b.bank_name} &middot; {b.account_holder_name}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
                      <div style={{ fontSize: 11, background: b.avs_verified ? "rgba(46,125,50,0.08)" : "rgba(201,168,76,0.12)", color: b.avs_verified ? "#2E7D32" : "#8a6a1a", borderRadius: 99, padding: "3px 10px", fontWeight: 700 }}>
                        {b.avs_verified ? "Verified manually" : "Not yet verified"}
                      </div>
                      {!b.avs_verified && (
                        <button onClick={() => markBankingVerified(b.id)} className="admin-btn-green">Mark verified manually</button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

      </div>

      {/* CONFIRM MODAL */}
      {confirmAction && (
        <div className="admin-modal-overlay" onClick={() => setConfirmAction(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#1A1A2E", marginBottom: 12 }}>Are you sure?</div>
            <div style={{ fontSize: 13, color: "#4A4A6A", marginBottom: 24, lineHeight: 1.6 }}>{confirmAction.label}</div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setConfirmAction(null)}
                style={{ flex: 1, background: "transparent", border: "1.5px solid #EDE8DF", borderRadius: 99, padding: "11px", color: "#888", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "var(--font-body)" }}
              >
                Cancel
              </button>
              <button
                onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}
                style={{ flex: 1, background: "#1B3A6B", border: "none", borderRadius: 99, padding: "11px", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "var(--font-body)" }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}